import {
  BarElement, CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale, Tooltip
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  useMemo
} from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { formatNumberWithSeparators } from 'common/utils/functions';
import KioskCardStatistics, { KioskCardStatisticsItem } from 'extends/KioskCardStatistics';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
);

interface Props {
  data?: any,
  options?: ChartOptions<'bar'>
  style?: React.CSSProperties
  cardItems?: KioskCardStatisticsItem[]
  dataOnTop?: number[]
}

const BAR_PERCENTAGE = 0.9;
const CATEGORY_PERCENTAGE = 0.9;

const KioskAtherosclerosisChart = ({
  data,
  dataOnTop,
  cardItems = [],
  options: customOptions,
  style
}: Props) => {
  const { t } = useTranslation();

  const dummyData = {
    labels: [
      t('dashboard2025.dotquyThieumau'),
      t('dashboard2025.nhoimaucotim'),
      t('dashboard2025.datstent'),
      t('dashboard2025.dauthatnguc'),
      t('dashboard2025.phinhtachdongmach'),
    ],
    datasets: [
      {
        data: [10, 35, 15, 20, 25],
        backgroundColor: '#C85693',
        barPercentage: BAR_PERCENTAGE,
        categoryPercentage: CATEGORY_PERCENTAGE,
      },
    ],
  };

  const transformedData = useMemo(() => {
    if (!data) {
      return dummyData;
    }
    const result = {
      ...dummyData,
      ...data,
      datasets: data.datasets.map((dataset: any) => ({
        ...dataset,
        backgroundColor: '#C85693',
        barPercentage: BAR_PERCENTAGE,
        categoryPercentage: CATEGORY_PERCENTAGE,
      })),
    };
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        labels: {
          top: {
            anchor: 'end', // attach to top of bar
            align: 'top',
            formatter: (value: number, ctx) => {
              const { dataIndex } = ctx;
              return formatNumberWithSeparators(dataOnTop?.[dataIndex]) || '';
            },
            font: {
              weight: 'bold',
              size: 16,
            },
            color: '#111',
          },
          inside: {
            color: 'white',
            anchor: 'center',
            align: 'center',
            formatter: (value: number) => (Number(value) > 2 ? `${value}%` : ''), // show % in each block
            font: {
              weight: 'bold' as const,
              size: 16
            },
          },
        }

      },
      tooltip: {
        callbacks: {
          // Custom label for each dataset
          label: (context: any) => {
            // const { datasetIndex } = context;
            const value = context.raw; // raw data value
            return `${value}%`; // e.g. "Part A: 35%"
          },
        },
      }
    },
    scales: {
      x: {
        offset: true,
        ticks: {
          color: '#667085',
          align: 'center',
          crossAlign: 'near',
          padding: 16,
          font: {
            weight: 'bold',
            size: 16
          }
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: false
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          color: '#667085',
          callback: (value: string | number) => `${value}%`,
          font: { weight: 'bold', size: 16 },
          padding: 10
        },
        grid: {
          drawBorder: false,
          drawTicks: false,
          borderDash: [4, 6],
        }
      },
    },
    ...customOptions
  };

  return (
    <div
      className="p-home_chart"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        ...style
      }}
    >
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#3D424F',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        {t('dashboard2025.atherosclerosisTitle')}
      </h1>
      <KioskCardStatistics
        style={{
          width: '100%',
          marginBottom: 20
        }}
        items={cardItems}
      />
      <div style={{
        width: '100%',
        height: '100%',
      }}
      >
        <Bar data={transformedData} options={options} />
      </div>
    </div>
  );
};

export default KioskAtherosclerosisChart;
